import React, { useEffect, useState, useRef } from 'react';
import Topbar from '../component/Topbar';
import { useLabels, useLang } from '../hooks/useLabels';
import { BiSearchAlt, BiPlus, BiSolidTrash, BiEditAlt, BiWorld } from "react-icons/bi";
import { PiMicrosoftExcelLogoDuotone } from "react-icons/pi";
import axios from 'axios';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import ReactPaginate from 'react-paginate';
import LoadingScreen from '../component/LoadingScreen';
import Swal from 'sweetalert2';
import { InteractionStatus } from "@azure/msal-browser";
import { acquireGetAccessToken } from '../utils/GetGrapApi';
import { Buffer } from 'buffer';
import { callMsGraph, checkRole, UserRole } from '../callMsGroup';
import { saveRealRole, getEffectiveRole } from '../utils/roleOverride';
import { useMsal } from "@azure/msal-react";

interface LanguageLabel {
    id: number;
    key: string;
    category: string;
    english: string;
    vietnamese: string;
}

interface LabelForm {
    id: number;
    key: string;
    category: string;
    english: string;
    vietnamese: string;
}

const emptyForm: LabelForm = { id: 0, key: '', category: '', english: '', vietnamese: '' };

function DeleteConfirmModal(props: any) {
    return (
        <Modal {...props} size="md" centered>
            <Modal.Header closeButton>
                <Modal.Title>{props.titleText || 'Confirm Delete'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>{props.bodyPrefix || 'Are you sure you want to delete Label'} <strong>"{props.labelKey}"</strong>?</p>
                <p className="text-danger mb-0"><small>{props.cannotUndoText || 'This action cannot be undone'}</small></p>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={props.onHide}>{props.cancelText || 'Cancel'}</Button>
                <Button variant="danger" onClick={props.onDelete}>{props.deleteText || 'Delete'}</Button>
            </Modal.Footer>
        </Modal>
    );
}

function LabelFormModal(props: any) {
    const { form, setForm, categories, onSave, saving } = props;
    const isEdit = !!form.id;

    return (
        <Modal {...props} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>{isEdit ? (props.editTitle || 'Edit Language Label') : (props.addTitle || 'Add Language Label')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="row g-3">
                    <div className="col-6">
                        <label className="form-label fw-bold">{props.keyLabel || 'Key'} <span className="text-danger">*</span></label>
                        <input
                            type="text"
                            className="form-control font-monospace"
                            value={form.key}
                            onChange={e => setForm((f: LabelForm) => ({ ...f, key: e.target.value.toUpperCase() }))}
                            placeholder="e.g. BUTTON_SAVE"
                            disabled={isEdit}
                        />
                        {isEdit && <small className="text-muted">{props.keyImmutableHint || 'Key cannot be changed after creation'}</small>}
                    </div>
                    <div className="col-6">
                        <label className="form-label fw-bold">{props.categoryLabel || 'Category'} <span className="text-danger">*</span></label>
                        <input
                            type="text"
                            className="form-control"
                            list="ll-category-list"
                            value={form.category}
                            onChange={e => setForm((f: LabelForm) => ({ ...f, category: e.target.value }))}
                            placeholder="e.g. BUTTON, LABEL, MESSAGE"
                        />
                        <datalist id="ll-category-list">
                            {categories.map((cat: string) => <option key={cat} value={cat} />)}
                        </datalist>
                    </div>
                    <div className="col-12">
                        <label className="form-label fw-bold">
                            <span className="me-1">🇬🇧</span> {props.englishLabel || 'English (EN)'}
                        </label>
                        <textarea
                            className="form-control"
                            rows={3}
                            value={form.english}
                            onChange={e => setForm((f: LabelForm) => ({ ...f, english: e.target.value }))}
                            placeholder="English text..."
                        />
                    </div>
                    <div className="col-12">
                        <label className="form-label fw-bold">
                            <span className="me-1">🇻🇳</span> {props.vietnameseLabel || 'Vietnamese (VN)'}
                        </label>
                        <textarea
                            className="form-control"
                            rows={3}
                            value={form.vietnamese}
                            onChange={e => setForm((f: LabelForm) => ({ ...f, vietnamese: e.target.value }))}
                            placeholder="Văn bản tiếng Việt..."
                        />
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={props.onHide} disabled={saving}>{props.cancelText || 'Cancel'}</Button>
                <Button variant="success" onClick={onSave} disabled={saving}>
                    {saving
                        ? <><span className="spinner-border spinner-border-sm me-2" />{props.savingText || 'Saving...'}</>
                        : (props.saveText || 'Save')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

function LanguageLabels() {
    const { inProgress, instance, accounts } = useMsal();

    // Auth
    const [token, setToken] = useState('');
    const [lang] = useLang();
    const { t } = useLabels('languagelabels', token, 'en');
    const [tokenProfile, setTokenProfile] = useState('');
    const [photoData, setPhotoData] = useState('');
    const [role, setRole] = useState<UserRole>('owner');
    const [stateLoad, setStateLoad] = useState(false);

    // Data
    const [labels, setLabels] = useState<LanguageLabel[]>([]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [categories, setCategories] = useState<string[]>([]);

    // Filter
    const [keyword, setKeyword] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [offset, setOffset] = useState(0);
    const limit = 20;

    // UI
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [isImporting, setIsImporting] = useState(false);

    // Modals
    const [formModal, setFormModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [form, setForm] = useState<LabelForm>(emptyForm);
    const [deleteTarget, setDeleteTarget] = useState<{ id: number; key: string }>({ id: 0, key: '' });

    const importInputRef = useRef<HTMLInputElement>(null);

    const authHeaders = () => ({
        headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + sessionStorage.getItem('accessToken'),
        },
    });

    const loadLabels = (kw = keyword, cat = categoryFilter, off = offset) => {
        setLoading(true);
        axios.get(`${process.env.REACT_APP_API_END_POINT}/v1/LanguageLabel/Labels`, {
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + sessionStorage.getItem('accessToken'),
            },
            params: { keyword: kw, category: cat, limit, offset: off },
        })
        .then(res => {
            const data = res.data;
            setLabels(data.labels ?? data.items ?? (Array.isArray(data) ? data : []));
            setTotal(data.total ?? 0);
            setTotalPages(data.totalPages ?? Math.ceil((data.total ?? 0) / limit));
        })
        .catch(() => {
            Swal.fire({ title: 'Error', text: 'Failed to load data', icon: 'error', confirmButtonText: 'OK' });
        })
        .finally(() => setLoading(false));
    };

    const loadCategories = () => {
        axios.get(`${process.env.REACT_APP_API_END_POINT}/v1/LanguageLabel/categories`, authHeaders())
        .then(res => setCategories(Array.isArray(res.data) ? res.data : []))
        .catch(() => {});
    };

    const handleSave = () => {
        if (!form.key.trim()) {
            Swal.fire({ title: 'Required', text: 'Please enter Key', icon: 'warning', confirmButtonText: 'OK' });
            return;
        }
        if (!form.category.trim()) {
            Swal.fire({ title: 'Required', text: 'Please enter Category', icon: 'warning', confirmButtonText: 'OK' });
            return;
        }

        setSaving(true);
        const isEdit = !!form.id;
        const req = isEdit
            ? axios.put(`${process.env.REACT_APP_API_END_POINT}/v1/LanguageLabel/Update`, form, authHeaders())
            : axios.post(`${process.env.REACT_APP_API_END_POINT}/v1/LanguageLabel/Create`, form, authHeaders());

        req.then(() => {
            setFormModal(false);
            setForm(emptyForm);
            loadLabels();
            loadCategories();
            Swal.fire({ title: 'Success', text: isEdit ? 'Updated successfully' : 'Added successfully', icon: 'success', timer: 1500, showConfirmButton: false });
        })
        .catch(() => {
            Swal.fire({ title: 'Error', text: 'Failed to save data. Please try again', icon: 'error', confirmButtonText: 'OK' });
        })
        .finally(() => setSaving(false));
    };

    const handleDelete = () => {
        setDeleteModal(false);
        axios.delete(`${process.env.REACT_APP_API_END_POINT}/v1/LanguageLabel/Delete`, {
            ...authHeaders(),
            params: { id: deleteTarget.id },
        })
        .then(() => {
            loadLabels();
            loadCategories();
            Swal.fire({ title: 'Success', text: 'Deleted successfully', icon: 'success', timer: 1500, showConfirmButton: false });
        })
        .catch(() => {
            Swal.fire({ title: 'Error', text: 'Failed to delete data', icon: 'error', confirmButtonText: 'OK' });
        });
    };

    const handleExport = () => {
        setIsExporting(true);
        axios.get(`${process.env.REACT_APP_API_END_POINT}/v1/LanguageLabel/ExportExcel`, {
            headers: { Authorization: 'Bearer ' + sessionStorage.getItem('accessToken') },
            responseType: 'blob',
        })
        .then(res => {
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = `LanguageLabels_${new Date().toISOString().split('T')[0]}.xlsx`;
            a.click();
            window.URL.revokeObjectURL(url);
        })
        .catch(() => {
            Swal.fire({ title: 'Error', text: 'Export failed', icon: 'error', confirmButtonText: 'OK' });
        })
        .finally(() => setIsExporting(false));
    };

    const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setIsImporting(true);
        const formData = new FormData();
        formData.append('file', file);
        axios.post(`${process.env.REACT_APP_API_END_POINT}/v1/LanguageLabel/ImportExcel`, formData, {
            headers: { Authorization: 'Bearer ' + sessionStorage.getItem('accessToken'), 'Content-Type': 'multipart/form-data' },
        })
        .then(() => {
            loadLabels();
            loadCategories();
            Swal.fire({ title: 'Import Success', text: 'Data imported successfully', icon: 'success', confirmButtonText: 'OK' });
        })
        .catch(err => {
            const msg = err.response?.data?.message || err.response?.data?.Error || 'Import failed. Please check the file format';
            Swal.fire({ title: 'Error', text: msg, icon: 'error', confirmButtonText: 'OK' });
        })
        .finally(() => {
            setIsImporting(false);
            if (importInputRef.current) importInputRef.current.value = '';
        });
    };

    const openCreate = () => { setForm(emptyForm); setFormModal(true); };
    const openEdit = (label: LanguageLabel) => {
        setForm({ id: label.id, key: label.key, category: label.category, english: label.english, vietnamese: label.vietnamese });
        setFormModal(true);
    };
    const openDelete = (label: LanguageLabel) => {
        setDeleteTarget({ id: label.id, key: label.key });
        setDeleteModal(true);
    };

    const handleSearch = () => {
        setOffset(0);
        loadLabels(keyword, categoryFilter, 0);
    };

    const handleClearSearch = () => {
        setKeyword('');
        setOffset(0);
        loadLabels('', categoryFilter, 0);
    };

    // Auth
    useEffect(() => {
        if (!token && inProgress === InteractionStatus.None) {
            acquireGetAccessToken(instance, accounts).then(async (res: any) => {
                if (!res) return;
                setToken(res.accessToken);
                setTokenProfile(res.accessTokenProfile);
                try {
                    const r = await axios('https://graph.microsoft.com/v1.0/me/photo/$value', {
                        headers: { Authorization: `Bearer ${res.accessTokenProfile}` },
                        responseType: 'arraybuffer',
                    });
                    setPhotoData('data:image/jpeg;base64, ' + Buffer.from(r.data, 'binary').toString('base64'));
                } catch {}
            });
        }
    }, [inProgress, token, instance, accounts]);

    // Role check
    useEffect(() => {
        if (!tokenProfile) return;
        const check = async () => {
            try {
                const userRole = await checkRole(tokenProfile);
                saveRealRole(userRole);
                setRole(getEffectiveRole(userRole));
                if (userRole === 'owner') window.location.href = `${window.location.origin}/accessdenied`;
                else setStateLoad(true);
            } catch {}
        };
        check();
    }, [tokenProfile]);

    // Load data on token ready / offset change
    useEffect(() => {
        if (!token) return;
        loadLabels(keyword, categoryFilter, offset);
        loadCategories();
    }, [token, offset]);

    // Reload when category filter changes
    useEffect(() => {
        if (!token) return;
        setOffset(0);
        loadLabels(keyword, categoryFilter, 0);
    }, [categoryFilter]);

    if (!stateLoad) return <LoadingScreen />;

    return (
        <>
            <div className="admin-layout">
                <Topbar token={token} photoData={photoData} role={role} />
                <main className="admin-main">
                    <div className="dt-container">

                        {/* Header */}
                        <div className="dt-header">
                            <div>
                                <div className="dt-title">{t('section.title', 'Language Labels')}</div>
                                <div className="dt-subtitle">{t('section.subtitle', 'Manage bilingual labels EN / VN')}</div>
                            </div>
                            <div className="d-flex gap-2 align-items-center">
                                <input
                                    type="file"
                                    ref={importInputRef}
                                    accept=".xlsx,.xls"
                                    style={{ display: 'none' }}
                                    onChange={handleImportFile}
                                />
                                <button
                                    className="btn btn-outline-success btn-sm"
                                    onClick={() => importInputRef.current?.click()}
                                    disabled={isImporting}
                                >
                                    {isImporting
                                        ? <><span className="spinner-border spinner-border-sm me-1" />{t('btn.importing', 'Importing...')}</>
                                        : <><PiMicrosoftExcelLogoDuotone size={16} className="me-1" />{t('btn.import', 'Import')}</>}
                                </button>
                                <button
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={handleExport}
                                    disabled={isExporting}
                                >
                                    {isExporting
                                        ? <><span className="spinner-border spinner-border-sm me-1" />{t('btn.exporting', 'Exporting...')}</>
                                        : <><PiMicrosoftExcelLogoDuotone size={16} className="me-1" />{t('btn.export', 'Export')}</>}
                                </button>
                                <button className="btn btn-add" onClick={openCreate}>
                                    <BiPlus size={18} /> {t('btn.add_label', 'Add Label')}
                                </button>
                            </div>
                        </div>

                        {/* Toolbar */}
                        <div className="dt-toolbar">
                            <div className="dt-search">
                                <BiSearchAlt className="dt-search-icon" />
                                <input
                                    type="text"
                                    className="dt-search-input"
                                    placeholder={t('placeholder.search_key_or_text', 'Search by Key or text...')}
                                    value={keyword}
                                    onChange={e => setKeyword(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
                                />
                                {keyword && (
                                    <button className="dt-search-clear" onClick={handleClearSearch}>×</button>
                                )}
                            </div>
                            <div className="d-flex gap-2 align-items-center">
                                <select
                                    className="form-select form-select-sm"
                                    style={{ minWidth: 180 }}
                                    value={categoryFilter}
                                    onChange={e => setCategoryFilter(e.target.value)}
                                >
                                    <option value="">{t('option.all', 'All')}</option>
                                    {categories.map(cat => (
                                        <option key={cat} value={cat}>{cat}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Table */}
                        <div className="dt-table-wrap">
                            <table className="dt-table ll-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: 44 }}>#</th>
                                        <th style={{ width: 240 }}>{t('table.col.key', 'Key')}</th>
                                        <th style={{ width: 140 }}>{t('table.col.category', 'Category')}</th>
                                        <th><span className="me-1">🇬🇧</span>EN</th>
                                        <th><span className="me-1">🇻🇳</span>VN</th>
                                        <th style={{ width: 72 }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan={6} className="text-center py-5 text-secondary">
                                                <span className="spinner-border spinner-border-sm me-2" />
                                                {t('state.loading', 'Loading...')}
                                            </td>
                                        </tr>
                                    ) : labels.length === 0 ? (
                                        <tr>
                                            <td colSpan={6} className="dt-empty">
                                                <div>{t('empty.title', 'No data found')}</div>
                                                <small>{t('empty.hint', 'Try changing keyword or category')}</small>
                                            </td>
                                        </tr>
                                    ) : labels.map((item, idx) => (
                                        <tr key={item.id}>
                                            <td className="text-secondary" style={{ fontSize: '0.75rem' }}>
                                                {offset + idx + 1}
                                            </td>
                                            <td>
                                                <code className="ll-key">{item.key}</code>
                                            </td>
                                            <td>
                                                <span className="ll-category-badge">{item.category}</span>
                                            </td>
                                            <td className="ll-value">{item.english || <span className="text-secondary">—</span>}</td>
                                            <td className="ll-value">{item.vietnamese || <span className="text-secondary">—</span>}</td>
                                            <td>
                                                <div className="dt-actions">
                                                    <button
                                                        className="dt-action-btn dt-action-btn--edit"
                                                        title="Edit"
                                                        onClick={() => openEdit(item)}
                                                    >
                                                        <BiEditAlt />
                                                    </button>
                                                    <button
                                                        className="dt-action-btn dt-action-btn--delete"
                                                        title="Delete"
                                                        onClick={() => openDelete(item)}
                                                    >
                                                        <BiSolidTrash />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Footer */}
                        <div className="dt-footer">
                            <div className="dt-count">
                                {t('label.showing', 'Showing')} {labels.length} {t('label.of', 'of')} {total} {t('label.labels', 'labels')}
                            </div>
                            {totalPages > 1 && (
                                <ReactPaginate
                                    previousLabel="‹"
                                    nextLabel="›"
                                    pageClassName="page-item"
                                    pageLinkClassName="page-link"
                                    previousClassName="page-item"
                                    previousLinkClassName="page-link"
                                    nextClassName="page-item"
                                    nextLinkClassName="page-link"
                                    breakLabel="..."
                                    breakClassName="page-item"
                                    breakLinkClassName="page-link"
                                    pageCount={totalPages}
                                    marginPagesDisplayed={1}
                                    pageRangeDisplayed={4}
                                    onPageChange={e => setOffset(e.selected * limit)}
                                    containerClassName="pagination mb-0"
                                    activeClassName="active"
                                    forcePage={Math.floor(offset / limit)}
                                />
                            )}
                        </div>

                    </div>
                </main>
            </div>

            <LabelFormModal
                show={formModal}
                onHide={() => { setFormModal(false); setForm(emptyForm); }}
                form={form}
                setForm={setForm}
                categories={categories}
                onSave={handleSave}
                saving={saving}
                editTitle={t('modal.edit_label', 'Edit Language Label')}
                addTitle={t('modal.add_label', 'Add Language Label')}
                keyLabel={t('form.key', 'Key')}
                keyImmutableHint={t('hint.key_immutable', 'Key cannot be changed after creation')}
                categoryLabel={t('form.category', 'Category')}
                englishLabel={t('form.english', 'English (EN)')}
                vietnameseLabel={t('form.vietnamese', 'Vietnamese (VN)')}
                cancelText={t('btn.cancel', 'Cancel')}
                saveText={t('btn.save', 'Save')}
                savingText={t('btn.saving', 'Saving...')}
            />
            <DeleteConfirmModal
                show={deleteModal}
                onHide={() => setDeleteModal(false)}
                onDelete={handleDelete}
                labelKey={deleteTarget.key}
                titleText={t('modal.confirm_delete', 'Confirm Delete')}
                bodyPrefix={t('modal.delete_label_prefix', 'Are you sure you want to delete Label')}
                cannotUndoText={t('modal.cannot_undo', 'This action cannot be undone')}
                cancelText={t('btn.cancel', 'Cancel')}
                deleteText={t('btn.delete', 'Delete')}
            />
        </>
    );
}

export default LanguageLabels;
